/**
 * Gallery state store.
 *
 * Holds:
 * - The current registry (loaded from loader)
 * - The selected module/bundle (for the inspector)
 * - The current data source ('live' or 'bundled')
 * - Loading + error state
 *
 * Uses zustand for minimal ceremony. No Redux, no provider boilerplate.
 */

import { create } from 'zustand';
import type { GalleryRegistry } from '../types/registry';
import { loadRegistry } from '../data/registry-loader';

type DataSource = 'live' | 'bundled' | 'pending' | 'error';

interface GalleryState {
  // Data
  registry: GalleryRegistry | null;
  source: DataSource;
  error: string | null;

  // Stats (derived, cached for performance)
  stats: GalleryRegistry['stats'] | null;

  // Selection (for the inspector / detail view)
  selectedModuleId: string | null;
  selectedBundleId: string | null;
  selectedFacet: string | null; // facet of the selected module

  // Filters
  searchQuery: string;
  selectedTag: string | null;
  selectedCategory: string | null;
  selectedTier: string | null;

  // Actions
  load: () => Promise<void>;
  reload: () => Promise<void>;
  selectModule: (id: string | null, facet?: string | null) => void;
  selectBundle: (id: string | null) => void;
  setSearchQuery: (q: string) => void;
  setSelectedTag: (tag: string | null) => void;
  setSelectedCategory: (cat: string | null) => void;
  setSelectedTier: (tier: string | null) => void;
}

export const useGallery = create<GalleryState>((set, get) => ({
  registry: null,
  source: 'pending',
  error: null,
  stats: null,

  selectedModuleId: null,
  selectedBundleId: null,
  selectedFacet: null,

  searchQuery: '',
  selectedTag: null,
  selectedCategory: null,
  selectedTier: null,

  async load() {
    set({ source: 'pending', error: null });
    try {
      const { data, source } = await loadRegistry(true);
      set({ registry: data, source, error: null, stats: data.stats });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      set({ source: 'error', error: msg });
    }
  },

  async reload() {
    return get().load();
  },

  selectModule(id, facet = null) {
    set({ selectedModuleId: id, selectedFacet: facet, selectedBundleId: null });
  },

  selectBundle(id) {
    set({ selectedBundleId: id, selectedModuleId: null, selectedFacet: null });
  },

  setSearchQuery(q) {
    set({ searchQuery: q });
  },

  setSelectedTag(tag) {
    set({ selectedTag: tag });
  },

  setSelectedCategory(cat) {
    set({ selectedCategory: cat });
  },

  setSelectedTier(tier) {
    set({ selectedTier: tier });
  },
}));

/**
 * Selectors — derived data, computed from the registry + filters.
 * Use these in components instead of computing inline.
 */

export function useFilteredModules() {
  return useGallery((s) => {
    if (!s.registry) return [];
    const q = s.searchQuery.trim().toLowerCase();
    return s.registry.modules.filter((m) => {
      if (s.selectedTag && !m.tags.includes(s.selectedTag)) return false;
      if (s.selectedCategory && m.category !== s.selectedCategory) return false;
      if (s.selectedTier && m.tier !== s.selectedTier) return false;
      if (q) {
        const hay = `${m.id} ${m.category} ${m.subcategory ?? ''} ${m.tags.join(' ')}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  });
}

export function useFilteredBundles() {
  return useGallery((s) => {
    if (!s.registry) return [];
    const q = s.searchQuery.trim().toLowerCase();
    return s.registry.bundles.filter((b) => {
      if (s.selectedTier && b.tier !== s.selectedTier) return false;
      if (q && !`${b.id} ${b.tier}`.toLowerCase().includes(q)) return false;
      return true;
    });
  });
}

export function useModule(id: string | null) {
  return useGallery((s) => {
    if (!s.registry || !id) return null;
    return s.registry.modules.find((m) => m.id === id) ?? null;
  });
}

export function useBundle(id: string | null) {
  return useGallery((s) => {
    if (!s.registry || !id) return null;
    return s.registry.bundles.find((b) => b.id === id) ?? null;
  });
}

export function useModuleFacets(id: string | null) {
  return useGallery((s) => {
    if (!s.registry || !id) return null;
    return s.registry.facet_index[id] ?? null;
  });
}

export function useModuleVariants(id: string | null) {
  return useGallery((s) => {
    if (!s.registry || !id) return [];
    return s.registry.variant_index[id] ?? [];
  });
}

export function useModuleConsumers(id: string | null) {
  return useGallery((s) => {
    if (!s.registry || !id) return [];
    const out: { app: string; facet: string | null }[] = [];
    for (const facet of Object.keys(s.registry.facet_index[id] ?? {})) {
      for (const app of s.registry.facet_index[id][facet].consumers) {
        out.push({ app, facet });
      }
    }
    return out;
  });
}

export function useModuleDependencies(id: string | null) {
  return useGallery((s) => {
    if (!s.registry || !id) return [];
    return s.registry.dependency_graph[id] ?? [];
  });
}
